import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format, isBefore, startOfDay, isAfter, addMonths, getDay } from "date-fns";
import {
  CalendarDays,
  Clock,
  User,
  Phone,
  Mail,
  FileText,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Stethoscope,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookAppointment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(false);
  const [form, setForm] = useState({
    patient_name: "",
    phone: "",
    email: "",
    notes: "",
  });

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data));
  }, []);

  useEffect(() => {
    if (selectedDate) {
      setLoadingSlots(true);
      setSelectedSlot("");
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      axios
        .get(`${API}/available-slots?date=${dateStr}`)
        .then((r) => setAvailableSlots(r.data.available_slots))
        .finally(() => setLoadingSlots(false));
    }
  }, [selectedDate]);

  const handleSubmit = async () => {
    if (!form.patient_name || !form.phone) {
      toast.error("Please fill in your name and phone number");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...form,
        service: selectedService,
        date: format(selectedDate, "yyyy-MM-dd"),
        time_slot: selectedSlot,
      });
      setBooked(true);
      toast.success("Appointment booked successfully!");
    } catch {
      toast.error("Failed to book appointment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const today = startOfDay(new Date());
  const maxDate = addMonths(today, 2);

  const disabledDays = (date) => {
    return isBefore(date, today) || isAfter(date, maxDate) || getDay(date) === 0;
  };

  if (booked) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 pt-20">
        <div className="max-w-md w-full text-center bg-white rounded-3xl p-10 border border-stone-100 shadow-sm" data-testid="booking-success">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-700" />
          </div>
          <h2 className="font-[Outfit] text-2xl font-bold text-stone-900 mb-3">Booking Confirmed!</h2>
          <p className="text-stone-600 mb-2">
            Your appointment for <strong>{selectedService}</strong> has been booked.
          </p>
          <p className="text-stone-600 mb-1">
            <strong>Date:</strong> {format(selectedDate, "EEEE, MMMM d, yyyy")}
          </p>
          <p className="text-stone-600 mb-6">
            <strong>Time:</strong> {selectedSlot}
          </p>
          <p className="text-sm text-stone-500 mb-8">
            Your appointment request has been received. Our team will contact you shortly to confirm. For any enquiries, please email <strong>info@ivmedicsclinic.com</strong> or call <strong>09162731000</strong>
          </p>
          <Button
            data-testid="back-to-home-btn"
            onClick={() => navigate("/")}
            className="bg-green-700 hover:bg-green-800 text-white rounded-full px-8 py-3"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge className="bg-green-100 text-green-800 border-0 mb-4 px-4 py-1.5 rounded-full text-sm font-medium" data-testid="booking-badge">
            Online Booking
          </Badge>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight mb-3">
            Book an Appointment
          </h1>
          <p className="text-stone-600 text-base md:text-lg max-w-lg mx-auto">
            Schedule your visit in a few simple steps. No account needed.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-10" data-testid="booking-steps">
          {[
            { num: 1, label: "Service", icon: Stethoscope },
            { num: 2, label: "Date & Time", icon: CalendarDays },
            { num: 3, label: "Your Details", icon: User },
          ].map((s, i) => (
            <div key={s.num} className="flex items-center gap-2">
              <div
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  step >= s.num
                    ? "bg-green-700 text-white"
                    : "bg-stone-200 text-stone-500"
                }`}
              >
                <s.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.num}</span>
              </div>
              {i < 2 && <div className={`w-8 h-0.5 ${step > s.num ? "bg-green-700" : "bg-stone-300"}`} />}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-3xl border border-stone-100 p-6 sm:p-10 shadow-sm">
          {/* Step 1: Select Service */}
          {step === 1 && (
            <div data-testid="step-1-service">
              <h3 className="font-[Outfit] text-xl font-semibold text-stone-900 mb-6">
                What service do you need?
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {services.map((svc) => (
                  <button
                    key={svc.id}
                    data-testid={`service-option-${svc.id}`}
                    onClick={() => setSelectedService(svc.name)}
                    className={`text-left p-4 rounded-2xl border-2 transition-all hover:border-green-600 ${
                      selectedService === svc.name
                        ? "border-green-700 bg-green-50"
                        : "border-stone-100 bg-white"
                    }`}
                  >
                    <p className="font-semibold text-stone-900 text-sm">{svc.name}</p>
                    <p className="text-xs text-stone-500 mt-1">{svc.category}</p>
                  </button>
                ))}
              </div>
              <div className="flex justify-end mt-8">
                <Button
                  data-testid="step-1-next-btn"
                  onClick={() => selectedService ? setStep(2) : toast.error("Please select a service")}
                  className="bg-green-700 hover:bg-green-800 text-white rounded-full px-8 py-3 gap-2"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <div data-testid="step-2-datetime">
              <h3 className="font-[Outfit] text-xl font-semibold text-stone-900 mb-6">
                Choose a date and time
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-3 block">Select Date</Label>
                  <div className="bg-stone-50 rounded-2xl p-4 flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={disabledDays}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-3 block">Select Time</Label>
                  {!selectedDate && (
                    <div className="bg-stone-50 rounded-2xl p-8 text-center text-stone-400">
                      <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">Please select a date first</p>
                    </div>
                  )}
                  {selectedDate && loadingSlots && (
                    <div className="bg-stone-50 rounded-2xl p-8 text-center text-stone-400">
                      <p className="text-sm">Loading available slots...</p>
                    </div>
                  )}
                  {selectedDate && !loadingSlots && (
                    <div className="grid grid-cols-3 gap-2 max-h-[320px] overflow-y-auto pr-1">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot}
                          data-testid={`time-slot-${slot.replace(/\s/g, "-")}`}
                          onClick={() => setSelectedSlot(slot)}
                          className={`px-3 py-2.5 rounded-xl text-xs font-medium transition-all ${
                            selectedSlot === slot
                              ? "bg-green-700 text-white"
                              : "bg-stone-50 text-stone-700 hover:bg-green-50 hover:text-green-700"
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                      {availableSlots.length === 0 && (
                        <p className="col-span-3 text-center text-stone-400 text-sm py-8">
                          No available slots for this date
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between mt-8">
                <Button
                  data-testid="step-2-back-btn"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="rounded-full px-6 gap-2 border-stone-200"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </Button>
                <Button
                  data-testid="step-2-next-btn"
                  onClick={() => {
                    if (!selectedDate) return toast.error("Please select a date");
                    if (!selectedSlot) return toast.error("Please select a time slot");
                    setStep(3);
                  }}
                  className="bg-green-700 hover:bg-green-800 text-white rounded-full px-8 gap-2"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Patient Details */}
          {step === 3 && (
            <div data-testid="step-3-details">
              <h3 className="font-[Outfit] text-xl font-semibold text-stone-900 mb-6">
                Your Details
              </h3>
              {/* Summary */}
              <div className="bg-green-50 rounded-2xl p-4 mb-8 flex flex-wrap gap-4 items-center text-sm">
                <div className="flex items-center gap-2 text-green-800">
                  <Stethoscope className="w-4 h-4" />
                  <span className="font-medium">{selectedService}</span>
                </div>
                <div className="flex items-center gap-2 text-green-800">
                  <CalendarDays className="w-4 h-4" />
                  <span>{selectedDate && format(selectedDate, "MMM d, yyyy")}</span>
                </div>
                <div className="flex items-center gap-2 text-green-800">
                  <Clock className="w-4 h-4" />
                  <span>{selectedSlot}</span>
                </div>
              </div>
              <div className="space-y-5">
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" /> Full Name *
                  </Label>
                  <Input
                    data-testid="patient-name-input"
                    placeholder="Enter your full name"
                    value={form.patient_name}
                    onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                    className="bg-stone-50 rounded-xl border-stone-200 h-12"
                  />
                </div>
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Phone Number *
                  </Label>
                  <Input
                    data-testid="patient-phone-input"
                    placeholder="e.g. 08012345678"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-stone-50 rounded-xl border-stone-200 h-12"
                  />
                </div>
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email (Optional)
                  </Label>
                  <Input
                    data-testid="patient-email-input"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-stone-50 rounded-xl border-stone-200 h-12"
                  />
                </div>
                <div>
                  <Label className="text-stone-700 text-sm font-medium mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Additional Notes (Optional)
                  </Label>
                  <Textarea
                    data-testid="patient-notes-input"
                    placeholder="Any symptoms or information you'd like to share..."
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="bg-stone-50 rounded-xl border-stone-200 min-h-[100px]"
                  />
                </div>
              </div>
              <div className="flex justify-between mt-8">
                <Button
                  data-testid="step-3-back-btn"
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="rounded-full px-6 gap-2 border-stone-200"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </Button>
                <Button
                  data-testid="confirm-booking-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 gap-2 shadow-lg"
                >
                  {submitting ? "Booking..." : "Confirm Booking"}
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
